import { useState, useEffect, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { useTransferOwnership } from '../../hooks/queries/useSpaceQueries';
import { ISpace, ISpaceMember } from '../../types';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import UserAutocomplete from '../UserAutocomplete';

interface ITransferOwnershipDialogProps {
  isOpen: boolean;
  onClose: () => void;
  space: ISpace;
}

const AVATAR_COLOR = '#3b82f6';

const TransferOwnershipDialog = ({
  isOpen,
  onClose,
  space,
}: ITransferOwnershipDialogProps) => {
  const { t } = useTranslation();
  const [newOwnerId, setNewOwnerId] = useState('');
  const [members, setMembers] = useState<ISpaceMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const transferOwnership = useTransferOwnership();

  useEffect(() => {
    if (!isOpen) return;

    api
      .get<ISpaceMember[]>(`/backoffice/spaces/${space.id}/members`)
      .then(setMembers)
      .catch(() => setMembers([]));
  }, [isOpen, space.id]);

  const fetchSpaceMembers = useCallback(
    async (search?: string) => {
      const ownerId = space.owner?.id || space.ownerId;
      const eligible = members.filter((m) => m.userId !== ownerId);

      const filtered = search
        ? eligible.filter(
            (m) =>
              m.user?.displayName
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
              m.user?.username?.toLowerCase().includes(search.toLowerCase()),
          )
        : eligible;

      return filtered.map((m) => ({
        id: m.userId,
        username: m.user?.username || m.userId,
        displayName: m.user?.displayName || m.user?.username || m.userId,
        avatar: m.user?.avatar,
      }));
    },
    [members, space.owner?.id, space.ownerId],
  );

  const handleSubmit = async () => {
    setError(null);

    try {
      await transferOwnership.mutateAsync({
        spaceId: space.id,
        ownerId: newOwnerId,
      });
      setNewOwnerId('');
      onClose();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleClose = () => {
    setNewOwnerId('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t('spaces.transfer_title')}</DialogTitle>
          <DialogDescription>
            {t('spaces.transfer_desc', { name: space.name })}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Current owner */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
              style={{ backgroundColor: AVATAR_COLOR }}
            >
              {(space.owner?.displayName || space.owner?.username || '?')
                .charAt(0)
                .toUpperCase()}
            </div>
            <div>
              <div className="text-xs text-blue-600 font-medium">
                {t('spaces.current_owner')}
              </div>
              <div className="text-sm text-blue-800 font-semibold">
                {space.owner?.displayName || space.owner?.username || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Select new owner */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            {t('spaces.select_new_owner')}
          </label>
          <UserAutocomplete
            value={newOwnerId}
            onChange={(userId) => setNewOwnerId(userId)}
            fetchUsers={fetchSpaceMembers}
            placeholder={t('spaces.search_users')}
            triggerPlaceholder={t('spaces.select_from_members')}
          />
        </div>

        {/* Important notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                {t('spaces.transfer_notice_title')}
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                {t('spaces.transfer_notice_text')}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!newOwnerId || transferOwnership.isPending}
          >
            {transferOwnership.isPending
              ? t('common.saving')
              : t('spaces.confirm_transfer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransferOwnershipDialog;
